import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useTheme } from '../../contexts/ThemeContext';
import {
  resolveCategoryOrder,
  resolveCategoryStyle,
  getCategoryBlueColor,
  formatHours,
  formatWorkloadLabel,
} from './constants';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler);

const CATEGORY_LABEL_KEYS = {
  HNO: 'summaryHno',
  HO: 'summaryHo',
  Astreinte: 'summaryAstreinte',
  Formation: 'summaryFormation',
  Congés: 'summaryConges',
  Absence: 'summaryAbsence',
};

function categoryLabel(t, category, fallback) {
  const key = CATEGORY_LABEL_KEYS[category];
  return key ? t(key) : fallback;
}


export default function WorkloadBalanceCard({ workloadBalance, categories = null }) {
  const { t } = useTranslation('planning');
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [activeTab, setActiveTab] = useState('detailed');
  const labels = workloadBalance?.labels || [];
  const categoryOrder = useMemo(() => resolveCategoryOrder(categories), [categories]);
  const categoryStyle = useMemo(() => resolveCategoryStyle(categories), [categories]);
  const seriesByCategory = useMemo(() => {
    const map = {};
    (workloadBalance?.series || []).forEach((item) => {
      map[item.category] = item.data || [];
    });
    return map;
  }, [workloadBalance]);
  const [hidden, setHidden] = useState(() => categoryOrder.map(() => false));

  useEffect(() => {
    setHidden((prev) => {
      if (prev.length === categoryOrder.length) return prev;
      return categoryOrder.map((_, i) => prev[i] || false);
    });
  }, [categoryOrder]);

  const includedCount = hidden.filter((isHidden) => !isHidden).length;

  // Sum only visible category hours so the label filter can exclude categories.
  const globalSeries = useMemo(() => {
    if (!labels.length) return [];
    return labels.map((_, index) => {
      const total = categoryOrder.reduce((sum, category) => {
        const categoryIndex = categoryOrder.indexOf(category);
        if (hidden[categoryIndex]) return sum;
        const values = seriesByCategory[category] || [];
        return sum + (Number(values[index]) || 0);
      }, 0);
      return Math.round(total * 10) / 10;
    });
  }, [hidden, labels, seriesByCategory, categoryOrder]);

  const globalTotalHours = useMemo(
    () => globalSeries.reduce((sum, value) => sum + (Number(value) || 0), 0),
    [globalSeries]
  );

  const detailedData = useMemo(
    () => ({
      labels: labels.map(formatWorkloadLabel),
      datasets: categoryOrder.map((category, index) => {
        const style = categoryStyle[category] || { label: category };
        const color = getCategoryBlueColor(index);
        return {
          label: categoryLabel(t, category, style.label),
          data: seriesByCategory[category] || labels.map(() => 0),
          borderColor: color,
          backgroundColor: `${color}22`,
          borderWidth: 2.5,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: color,
          fill: false,
          hidden: hidden[index],
        };
      }),
    }),
    [hidden, labels, seriesByCategory, categoryOrder, categoryStyle, t]
  );

  const globalData = useMemo(
    () => ({
      labels: labels.map(formatWorkloadLabel),
      datasets: [
        {
          label: t('totalHours'),
          data: globalSeries,
          borderColor: '#124191',
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return 'rgba(18, 65, 145, 0.12)';
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(18, 65, 145, 0.22)');
            gradient.addColorStop(1, 'rgba(18, 65, 145, 0.02)');
            return gradient;
          },
          borderWidth: 3,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: '#124191',
          fill: true,
        },
      ],
    }),
    [globalSeries, labels]
  );

  const options = useMemo(() => {
    const textColor = isDark ? '#94a3b8' : '#617289';
    const gridColor = isDark ? '#334155' : '#f1f5f9';
    const tooltipBg = isDark ? '#1e293b' : '#111418';
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: tooltipBg,
          titleColor: '#ffffff',
          bodyColor: '#e2e8f0',
          padding: 10,
          displayColors: true,
          boxPadding: 4,
          filter: (tooltipItem) =>
            activeTab === 'glob' ? true : !hidden[tooltipItem.datasetIndex],
          callbacks: {
            label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y} hrs`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: gridColor, drawBorder: false },
          ticks: { color: textColor, font: { size: 11, weight: '500' } },
        },
        y: {
          beginAtZero: true,
          grid: { color: gridColor, drawBorder: false },
          ticks: {
            color: textColor,
            font: { size: 11 },
            callback: (value) => `${value}h`,
          },
          title: {
            display: true,
            text: t('hoursAxis'),
            color: textColor,
            font: { size: 11, weight: '600' },
          },
        },
      },
    };
  }, [activeTab, hidden, isDark]);

  const hasData = labels.length > 0;

  return (
    <div className="workload-balance-card bg-white p-6 rounded-xl border border-[#e5e7eb] shadow-sm min-w-0">
      <div className="flex flex-col gap-4 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-primary shrink-0">
                <span className="material-symbols-outlined text-[18px]">monitoring</span>
              </span>
              <h3 className="text-[#111418] text-lg font-bold truncate">{t('workloadBalanceIndex')}</h3>
            </div>
            <p className="text-sm text-[#617289] pl-10">
              {activeTab === 'glob'
                ? t('workloadBalanceHintFiltered')
                : t('workloadBalanceHintDefault')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0 sm:justify-end">
            <div className="workload-view-tabs" role="tablist" aria-label={t('workloadView')}>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'detailed'}
                className={`workload-view-tab${activeTab === 'detailed' ? ' is-active' : ''}`}
                onClick={() => setActiveTab('detailed')}
              >
                <span className="material-symbols-outlined text-[16px]">stacked_line_chart</span>
                {t('detailed')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'glob'}
                className={`workload-view-tab${activeTab === 'glob' ? ' is-active' : ''}`}
                onClick={() => setActiveTab('glob')}
              >
                <span className="material-symbols-outlined text-[16px]">show_chart</span>
                {t('overview')}
              </button>
            </div>

          </div>
        </div>

        <div className="workload-label-filter">
          <div className="workload-label-filter-meta">
            <span className="workload-label-filter-title">{t('labels')}</span>
            <span className="workload-label-filter-count">
              {t('includedCount', { included: includedCount, total: categoryOrder.length })}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categoryOrder.map((category, index) => {
              const style = categoryStyle[category] || { label: category };
              const color = getCategoryBlueColor(index);
              const isExcluded = hidden[index];
              return (
                <button
                  key={category}
                  type="button"
                  className={`workload-label-chip${isExcluded ? ' is-excluded' : ''}`}
                  title={isExcluded ? t('includeLabel', { label: categoryLabel(t, category, style.label) }) : t('excludeLabel', { label: categoryLabel(t, category, style.label) })}
                  aria-pressed={!isExcluded}
                  onClick={() => {
                    setHidden((prev) => {
                      const next = [...prev];
                      next[index] = !next[index];
                      return next;
                    });
                  }}
                >
                  <span
                    className="legend-swatch"
                    style={{ backgroundColor: isExcluded ? '#cbd5e1' : color }}
                  />
                  <span>{categoryLabel(t, category, style.label)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="workload-chart-panel relative h-64 w-full">
        {hasData ? (
          <Line data={activeTab === 'glob' ? globalData : detailedData} options={options} />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6">
            <span className="material-symbols-outlined text-[28px] text-[#94a3b8]">insights</span>
            <p className="text-sm font-semibold text-[#617289]">{t('noWorkloadData')}</p>
            <p className="text-xs text-[#94a3b8]">
              Adjust date, centre, team, or member filters to see trends.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
